import { DownloadCloud, Search, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ParsedReportModal } from "../components/ParsedReportModal";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { clients as clientsApi, reports as reportsApi, type ClientListItem, type CreditReport } from "../lib/api";

export default function ReportsPage() {
  const [list, setList] = useState<CreditReport[]>([]);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [pickClient, setPickClient] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [viewReportId, setViewReportId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try { setList(await reportsApi.list()); } catch {}
    try {
      const r = await clientsApi.list({ limit: 200 });
      setClients(r.items);
    } catch {}
  }
  useEffect(() => { load(); }, []);

  async function upload(file: File) {
    if (!pickClient) { setError("Pick a client first"); return; }
    setUploading(true); setError(null); setInfo(null);
    try {
      await reportsApi.upload(pickClient, file);
      await load();
    } catch (e: any) { setError(e?.message); }
    finally { setUploading(false); }
  }

  async function importFromCredentials() {
    if (!pickClient) { setError("Pick a client first"); return; }
    setImporting(true); setError(null); setInfo(null);
    try {
      const result: any = await reportsApi.importFromCredentials(pickClient);
      const report = result?.report ?? result;
      const scores = result?.scores ?? null;
      if (report?.parse_status === "failed") {
        const err = (report?.parse_error || "").toLowerCase();
        if (err.includes("rate limit") || err.includes("429")) {
          setError("Cloudflare browser rate-limit hit — wait a few minutes and try again. (Free tier; upgrade Cloudflare Workers Paid to remove this limit.)");
        } else if (err.includes("invalid email or password") || err.includes("auth_failed")) {
          setError("MyFreeScoreNow rejected the credentials. Verify the email and password on the client's record.");
        } else if (err.includes("timeout")) {
          setError("Import timed out. MFSN may be slow right now — try again in a few minutes.");
        } else {
          setError(`Import failed: ${report?.parse_error || "unknown error"}`);
        }
      } else if (report?.parse_status === "needs_manual") {
        setInfo("Imported — raw report saved. Score auto-extraction didn't run (the analyzer will still use the HTML).");
      } else {
        const bits: string[] = [];
        if (scores?.equifax)    bits.push(`Equifax ${scores.equifax}`);
        if (scores?.experian)   bits.push(`Experian ${scores.experian}`);
        if (scores?.transunion) bits.push(`TransUnion ${scores.transunion}`);
        setInfo(bits.length ? `Imported. Scores: ${bits.join(" · ")}` : "Imported successfully.");
      }
      await load();
    } catch (e: any) {
      setError(e?.message || "Import failed");
    } finally {
      setImporting(false);
    }
  }

  const noClient = !pickClient;
  const importTooltip = noClient
    ? "Pick a client before importing"
    : "Pulls the credit report using the client's saved MyFreeScoreNow or SmartCredit credentials";

  return (
    <Shell title="Credit Reports" subtitle="Imports" actions={<span />}>
      <div className="panel">
        <div className="panel-header">
          <div><h2>Import</h2></div>
        </div>
        <div className="form-row">
          <select
            value={pickClient}
            onChange={(e) => setPickClient(e.target.value)}
            aria-label="Filter reports by client"
          >
            <option value="">Select a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.legal_name}</option>
            ))}
          </select>
          <button
            className="primary-button"
            onClick={importFromCredentials}
            disabled={noClient || importing || uploading}
            title={importTooltip}
          >
            <DownloadCloud size={16} />{" "}
            {importing ? "Importing… this can take up to 90 seconds" : "Import from Credentials"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
          />
          <button
            className="primary-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || importing}
          >
            <Upload size={16} /> {uploading ? "Uploading…" : "Upload PDF"}
          </button>
        </div>
        {error && <p className="error-text">{error}</p>}
        {info && <p className="muted small">{info}</p>}
      </div>

      <div className="panel">
        <div className="panel-header">
          <div><h2>Imported reports</h2></div>
        </div>
        <div className="table-list">
          <div className="table-row" style={{ fontWeight: 600, fontSize: 12, color: "var(--ne-muted)", textTransform: "uppercase", letterSpacing: 0.4, gridTemplateColumns: "64px minmax(100px, 1fr) 90px 88px 130px 70px minmax(60px, 1fr) 48px" }}>
            <span>Report ID</span>
            <span>Client</span>
            <span>Provider</span>
            <span>Pulled</span>
            <span>Imported</span>
            <span>Status</span>
            <span>Error</span>
            <span>View</span>
          </div>
          {list.length === 0 && <p className="muted small">No reports yet.</p>}
          {list.map((r) => {
            const importedFull = r.imported_at ? new Date(r.imported_at) : null;
            const importedLabel = importedFull
              ? `${importedFull.toLocaleDateString()} ${importedFull.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
              : "—";
            return (
              <div className="table-row" key={r.id} style={{ gridTemplateColumns: "64px minmax(100px, 1fr) 90px 88px 130px 70px minmax(60px, 1fr) 48px" }}>
                <span>{r.id.slice(0, 8)}</span>
                <strong>{r.client_id.slice(0, 8)}</strong>
                <span>{r.provider}</span>
                <span>{r.pulled_at || "—"}</span>
                <span title={importedFull?.toString()}>{importedLabel}</span>
                <StatusPill status={r.parse_status} />
                <span>{r.parse_error || "—"}</span>
                <span>
                  <button
                    className="icon-button"
                    title="View report"
                    aria-label="View report"
                    data-testid={`view-parsed-${r.id}`}
                    onClick={() => setViewReportId(r.id)}
                    style={{ height: 28, padding: "0 8px" }}
                  >
                    <Search size={14} />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <ParsedReportModal
        open={!!viewReportId}
        reportId={viewReportId}
        onClose={() => setViewReportId(null)}
      />
    </Shell>
  );
}
